import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  TextField,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { collection, addDoc, getDocs, query, where, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { firestore } from './firebase.config';
import { useAuth } from './AuthContext';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const UserManagement = () => {
  const { isSuperAdmin: isSuperAdminFromContext, currentUser } = useAuth();
  const [isAppAdmin, setIsAppAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(isSuperAdminFromContext);
  const [tabValue, setTabValue] = useState(0);
  const [adminTenantInfo, setAdminTenantInfo] = useState(null);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [userFormData, setUserFormData] = useState({
    userId: '',
    userName: '',
    userGroup: '',
    userEmailId: '',
    userMobileNo: '',
    role: 'AppUser',
  });

  const [groupFormData, setGroupFormData] = useState({
    userGrpId: '',
    userGrpName: '',
    userRole: 'AppUser',
  });

  const [users, setUsers] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [lastAction, setLastAction] = useState('');

  useEffect(() => {
    if (typeof isSuperAdminFromContext === 'undefined' && currentUser && currentUser.email) {
      const superAdminEmails = [
        process.env.REACT_APP_ADMIN_EMAIL1,
        process.env.REACT_APP_ADMIN_EMAIL2,
        process.env.REACT_APP_ADMIN_EMAIL3
      ].filter(Boolean);
      setIsSuperAdmin(superAdminEmails.includes(currentUser.email));
    } else {
      setIsSuperAdmin(isSuperAdminFromContext);
    }
  }, [isSuperAdminFromContext, currentUser]);

  useEffect(() => {
    console.log('isSuperAdmin:', isSuperAdmin, 'currentUser:', currentUser);
  }, [isSuperAdmin, currentUser]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchUsers();
      fetchUserGroups();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!currentUser || isSuperAdmin) return;
    // Fetch user profile from Firestore to get role and tenant info
    const fetchProfile = async () => {
      const userProfileRef = collection(firestore, 'UserProfileMaster');
      const q = query(userProfileRef, where('email', '==', currentUser.email));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const userProfile = querySnapshot.docs[0].data();
        if (userProfile.role === 'AppAdmin') {
          setIsAppAdmin(true);
          setAdminTenantInfo({
            tenantId: userProfile.tenantId,
            tenantName: userProfile.tenantName
          });
        }
      }
    };
    fetchProfile();
  }, [currentUser, isSuperAdmin]);

  useEffect(() => {
    if (adminTenantInfo) {
      console.log("Admin tenant info updated, fetching data...");
      fetchUsers();
      fetchUserGroups();
    }
  }, [adminTenantInfo]);

  // Fetch available tenants for super admin
  useEffect(() => {
    const fetchTenants = async () => {
      if (!isSuperAdmin) return;

      try {
        const tenantsRef = collection(firestore, "Tenants");
        const querySnapshot = await getDocs(tenantsRef);
        const tenantsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAvailableTenants(tenantsData);

        // Restore selected tenant from session storage if exists
        const storedTenantId = sessionStorage.getItem('selectedTenantId');
        if (storedTenantId) {
          setSelectedTenantId(storedTenantId);
          const selectedTenant = tenantsData.find(t => t.tenantId === storedTenantId);
          if (selectedTenant) {
            setAdminTenantInfo(selectedTenant);
          }
        }
      } catch (error) {
        console.error("Error fetching tenants:", error);
      }
    };

    fetchTenants();
  }, [isSuperAdmin]);

  // Handle tenant selection change
  const handleTenantChange = (event) => {
    const tenantId = event.target.value;
    setSelectedTenantId(tenantId);
    
    // Store selected tenant in session storage
    sessionStorage.setItem('selectedTenantId', tenantId);
    
    // Update admin tenant info
    const selectedTenant = availableTenants.find(t => t.tenantId === tenantId);
    if (selectedTenant) {
      setAdminTenantInfo(selectedTenant);
    }
  };

  const fetchUsers = async () => {
    try {
      const usersRef = collection(firestore, "UserProfileMaster");
      let queryRef;
      if (isSuperAdmin) {
        if (!selectedTenantId) return;
        queryRef = query(usersRef, where("tenantId", "==", selectedTenantId));
      } else if (adminTenantInfo) {
        queryRef = query(usersRef, where("tenantId", "==", adminTenantInfo.tenantId));
      } else {
        return;
      }
      const querySnapshot = await getDocs(queryRef);
      const usersData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          userId: doc.id,
          userName: data.displayName || '',
          userEmailId: data.email || '',
          userMobileNo: data.mobileNo || '',
          tenantId: data.tenantId,
          role: data.role,
          ...data
        };
      });
      setUsers(usersData);
    } catch (error) {
      setErrors({ submission: "Failed to fetch users. Please try again." });
    }
  };

  const fetchUserGroups = async () => {
    try {
      const groupsRef = collection(firestore, "UserProfileMaster");
      let queryRef;
      if (isSuperAdmin) {
        if (!selectedTenantId) return;
        queryRef = query(groupsRef, where("role", "==", "userGroup"), where("tenantId", "==", selectedTenantId));
      } else if (adminTenantInfo) {
        queryRef = query(groupsRef, where("role", "==", "userGroup"), where("tenantId", "==", adminTenantInfo.tenantId));
      } else {
        return;
      }
      const querySnapshot = await getDocs(queryRef);
      const groupsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        userGrpId: doc.data().userGrpId || doc.id,
        userGrpName: doc.data().displayName || ''
      }));
      setUserGroups(groupsData);
    } catch (error) {
      setErrors({ submission: "Failed to fetch user groups. Please try again." });
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSelectedItem(null);
    setErrors({});
    setSubmitSuccess(false);
  };

  const handleUserFormChange = (e) => {
    const { name, value } = e.target;
    setUserFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGroupFormChange = (e) => {
    const { name, value } = e.target;
    setGroupFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateUserForm = () => {
    let tempErrors = {};
    let isValid = true;

    // if (!userFormData.userId) {
    //   tempErrors.userId = 'User ID is required';
    //   isValid = false;
    // }

    if (!userFormData.userName) {
      tempErrors.userName = 'User Name is required';
      isValid = false;
    }

    if (!userFormData.userEmailId) {
      tempErrors.userEmailId = 'Email ID is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(userFormData.userEmailId)) {
      tempErrors.userEmailId = 'Invalid email format';
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const validateGroupForm = () => {
    let tempErrors = {};
    let isValid = true;

    if (!groupFormData.userGrpId) {
      tempErrors.userGrpId = 'User Group ID is required';
      isValid = false;
    }

    if (!groupFormData.userGrpName) {
      tempErrors.userGrpName = 'User Group Name is required';
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    if (!validateUserForm()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      const userProfileData = {
        email: userFormData.userEmailId,
        displayName: userFormData.userName,
        userGroup: userFormData.userGroup,
        mobileNo: userFormData.userMobileNo,
        role: userFormData.role,
        status: 'active',
        ...((isSuperAdmin && adminTenantInfo) && {
          tenantId: adminTenantInfo.tenantId,
          tenantName: adminTenantInfo.tenantName
        }),
        ...(isAppAdmin && adminTenantInfo && {
          tenantId: adminTenantInfo.tenantId,
          tenantName: adminTenantInfo.tenantName
        }),
        createdAt: new Date().toISOString()
      };

      console.log("Creating user with data:", userProfileData);

      if (selectedItem) {
        await updateDoc(doc(firestore, "UserProfileMaster", selectedItem.id), {
          ...userProfileData,
          updatedAt: new Date().toISOString()
        });
      } else {
        const docRef = await addDoc(collection(firestore, "UserProfileMaster"), userProfileData);
        console.log("Created user with ID:", docRef.id);
      }

      setUserFormData({
        userId: '',
        userName: '',
        userGroup: '',
        userEmailId: '',
        userMobileNo: '',
        role: 'AppUser',
      });
      setSelectedItem(null);
      setSubmitSuccess(true);
      setLastAction('update');
      
      await fetchUsers();
      
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (error) {
      console.error("Error managing user:", error);
      setErrors({ submission: "Operation failed. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    if (!validateGroupForm()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      const groupData = {
        userGrpId: groupFormData.userGrpId,
        displayName: groupFormData.userGrpName,
        role: 'userGroup',
        userRole: groupFormData.userRole,
        ...(adminTenantInfo && {
          tenantId: adminTenantInfo.tenantId,
          tenantName: adminTenantInfo.tenantName
        }),
        createdAt: new Date().toISOString()
      };

      if (selectedItem) {
        await updateDoc(doc(firestore, "UserProfileMaster", selectedItem.id), {
          ...groupData,
          updatedAt: new Date().toISOString()
        });
      } else {
        await addDoc(collection(firestore, "UserProfileMaster"), groupData);
      }

      setGroupFormData({
        userGrpId: '',
        userGrpName: '',
        userRole: 'AppUser',
      });
      setSelectedItem(null);
      setSubmitSuccess(true);
      setLastAction('update');
      fetchUserGroups();

      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (error) {
      console.error("Error managing user group:", error);
      setErrors({ submission: "Operation failed. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    if (tabValue === 0) {
      setUserFormData({
        userId: item.userId,
        userName: item.userName,
        userGroup: item.userGroup,
        userEmailId: item.userEmailId,
        userMobileNo: item.userMobileNo,
        role: item.role,
      });
    } else {
      setGroupFormData({
        userGrpId: item.userGrpId,
        userGrpName: item.userGrpName,
        userRole: item.userRole,
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item? This action cannot be undone.')) {
      try {
        setIsSubmitting(true);
        await deleteDoc(doc(firestore, "UserProfileMaster", id));
        setLastAction('delete');
        
        // Update local state to remove the deleted item
        if (tabValue === 0) {
          setUsers(prevUsers => prevUsers.filter(user => user.userId !== id));
        } else {
          setUserGroups(prevGroups => prevGroups.filter(group => group.id !== id));
        }

        setSubmitSuccess(true);
        setTimeout(() => setSubmitSuccess(false), 3000);
      } catch (error) {
        console.error("Error deleting item:", error);
        setErrors({
          submission: "Failed to delete. Please try again."
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          User Management
        </Typography>

        {/* Tenant Selector for Super Admin */}
        {isSuperAdmin && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Select Tenant</InputLabel>
              <Select
                value={selectedTenantId}
                onChange={handleTenantChange}
                label="Select Tenant"
                required
              >
                <MenuItem value="">
                  <em>Select a tenant</em>
                </MenuItem>
                {availableTenants.map((tenant) => (
                  <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                    {tenant.tenantName} ({tenant.tenantId})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>
        )}

        {/* Show warning if no tenants exist */}
        {isSuperAdmin && availableTenants.length === 0 && (
          <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: '#ffebee', borderRadius: 1 }}>
            <Typography color="error">
              No tenants found. Please add a tenant in Tenant Management.
            </Typography>
          </Paper>
        )}

        {/* Show warning if no tenant is selected for super admin */}
        {isSuperAdmin && availableTenants.length > 0 && !selectedTenantId && (
          <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: '#fff3e0', borderRadius: 1 }}>
            <Typography color="warning.main">
              Please select a tenant to manage users.
            </Typography>
          </Paper>
        )}

        {/* Only show tabs and content if tenant is selected or not super admin */}
        {(isSuperAdmin && selectedTenantId) || isAppAdmin ? (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab label="User List" />
                {/* <Tab label="User Group" /> */}
              </Tabs>
            </Box>

            {submitSuccess && (
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  mb: 3, 
                  backgroundColor: '#e8f5e9',
                  borderRadius: 1
                }}
              >
                <Typography color="success.main">
                  {lastAction === 'delete'
                    ? 'User deleted successfully!'
                    : selectedItem
                      ? 'Updated successfully!'
                      : 'Added successfully!'}
                </Typography>
              </Paper>
            )}

            {errors.submission && (
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  mb: 3, 
                  backgroundColor: '#ffebee',
                  borderRadius: 1
                }}
              >
                <Typography color="error">
                  {errors.submission}
                </Typography>
              </Paper>
            )}

            <TabPanel value={tabValue} index={0}>
              <Paper sx={{ p: 3, mb: 3 }}>
                <form onSubmit={handleUserSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth error={!!errors.userName}>
                        <TextField
                          label="User Name"
                          name="userName"
                          value={userFormData.userName}
                          onChange={handleUserFormChange}
                          required
                          error={!!errors.userName}
                          helperText={errors.userName}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Role</InputLabel>
                        <Select
                          name="role"
                          value={userFormData.role}
                          onChange={handleUserFormChange}
                          label="Role"
                          required
                        >
                          <MenuItem value="AppUser">App User</MenuItem>
                          <MenuItem value="AppAdmin">App Admin</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth error={!!errors.userEmailId}>
                        <TextField
                          label="User Email ID"
                          name="userEmailId"
                          value={userFormData.userEmailId}
                          onChange={handleUserFormChange}
                          required
                          type="email"
                          error={!!errors.userEmailId}
                          helperText={errors.userEmailId}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <TextField
                          label="User Mobile No"
                          name="userMobileNo"
                          value={userFormData.userMobileNo}
                          onChange={handleUserFormChange}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        sx={{ mr: 2 }}
                      >
                        {selectedItem ? 'Update User' : 'Add User'}
                      </Button>
                      {selectedItem && (
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setSelectedItem(null);
                            setUserFormData({
                              userId: '',
                              userName: '',
                              userGroup: '',
                              userEmailId: '',
                              userMobileNo: '',
                              role: 'AppUser',
                            });
                          }}
                        >
                          Cancel Edit
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </form>
              </Paper>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User ID</TableCell>
                      <TableCell>User Name</TableCell>
                      <TableCell>User Email ID</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.userId}>
                        <TableCell>{user.userId}</TableCell>
                        <TableCell>{user.userName}</TableCell>
                        <TableCell>{user.userEmailId}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton color="primary" onClick={() => handleEdit(user)}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton color="error" onClick={() => handleDelete(user.userId)}>
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>
            {/* commented on 26th may 2025 */}
            {/* <TabPanel value={tabValue} index={1}>
              <Paper sx={{ p: 3, mb: 3 }}>
                <form onSubmit={handleGroupSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth error={!!errors.userGrpId}>
                        <TextField
                          label="User Group ID"
                          name="userGrpId"
                          value={groupFormData.userGrpId}
                          onChange={handleGroupFormChange}
                          required
                          error={!!errors.userGrpId}
                          helperText={errors.userGrpId}
                          disabled={!!selectedItem}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth error={!!errors.userGrpName}>
                        <TextField
                          label="User Group Name"
                          name="userGrpName"
                          value={groupFormData.userGrpName}
                          onChange={handleGroupFormChange}
                          required
                          error={!!errors.userGrpName}
                          helperText={errors.userGrpName}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>User Role</InputLabel>
                        <Select
                          name="userRole"
                          value={groupFormData.userRole}
                          onChange={handleGroupFormChange}
                          label="User Role"
                        >
                          <MenuItem value="AppUser">AppUser</MenuItem>
                          <MenuItem value="AppAdmin">Admin</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        sx={{ mr: 2 }}
                      >
                        {selectedItem ? 'Update Group' : 'Add Group'}
                      </Button>
                      {selectedItem && (
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setSelectedItem(null);
                            setGroupFormData({
                              userGrpId: '',
                              userGrpName: '',
                              userRole: 'AppUser',
                            });
                          }}
                        >
                          Cancel Edit
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </form>
              </Paper>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User Group ID</TableCell>
                      <TableCell>User Group Name</TableCell>
                      <TableCell>User Role</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userGroups.map((group) => (
                      <TableRow key={group.id}>
                        <TableCell>{group.userGrpId}</TableCell>
                        <TableCell>{group.userGrpName}</TableCell>
                        <TableCell>{group.userRole}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton color="primary" onClick={() => handleEdit(group)}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton color="error" onClick={() => handleDelete(group.id)}>
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel> */}
          </>
        ) : null}
      </Box>
    </Container>
  );
};

export default UserManagement; 